/**
 * ESC/POS — generación PURA del payload de impresión de tickets.
 *
 * Este módulo NO habla con ninguna impresora: produce los bytes ESC/POS
 * (Epson-compatible, el estándar de facto de impresoras térmicas de 58/80 mm)
 * listos para enviarse por el canal que sea (USB raw, red 9100, RawBT, Web
 * Serial…). El envío al dispositivo físico es una fase manual fuera de este
 * repo; aquí garantizamos que el payload es correcto y testeable en CI.
 *
 * Decisiones:
 * - Codepage CP858 (`ESC t 19`): Latin-1 + `€`, cubre es/pt/fr/de. Los
 *   caracteres fuera de tabla degradan a `?` (nunca bytes inválidos).
 * - Sin imágenes/QR en F1 (80/20): texto, énfasis, doble tamaño, corte y
 *   apertura de cajón.
 */

const ESC = 0x1b;
const GS = 0x1d;

/** CP858: mapa de los code points > 0x7F que usamos (subset Latin/€). */
const CP858: Record<number, number> = {
  0xe7: 0x87, // ç
  0xfc: 0x81, // ü
  0xe9: 0x82, // é
  0xe2: 0x83, // â
  0xe4: 0x84, // ä
  0xe0: 0x85, // à
  0xe5: 0x86, // å
  0xea: 0x88, // ê
  0xeb: 0x89, // ë
  0xe8: 0x8a, // è
  0xef: 0x8b, // ï
  0xee: 0x8c, // î
  0xec: 0x8d, // ì
  0xc4: 0x8e, // Ä
  0xc5: 0x8f, // Å
  0xc9: 0x90, // É
  0xe6: 0x91, // æ
  0xc6: 0x92, // Æ
  0xf4: 0x93, // ô
  0xf6: 0x94, // ö
  0xf2: 0x95, // ò
  0xfb: 0x96, // û
  0xf9: 0x97, // ù
  0xff: 0x98, // ÿ
  0xd6: 0x99, // Ö
  0xdc: 0x9a, // Ü
  0xf8: 0x9b, // ø
  0xa3: 0x9c, // £
  0xd8: 0x9d, // Ø
  0xd7: 0x9e, // ×
  0xe1: 0xa0, // á
  0xed: 0xa1, // í
  0xf3: 0xa2, // ó
  0xfa: 0xa3, // ú
  0xf1: 0xa4, // ñ
  0xd1: 0xa5, // Ñ
  0xaa: 0xa6, // ª
  0xba: 0xa7, // º
  0xbf: 0xa8, // ¿
  0xa1: 0xad, // ¡
  0xc1: 0xb5, // Á
  0xc2: 0xb6, // Â
  0xc0: 0xb7, // À
  0xa9: 0xb8, // ©
  0xa2: 0xbd, // ¢
  0xa5: 0xbe, // ¥
  0xe3: 0xc6, // ã
  0xc3: 0xc7, // Ã
  0x20ac: 0xd5, // €
  0xf0: 0xd0, // ð
  0xd0: 0xd1, // Ð
  0xca: 0xd2, // Ê
  0xcb: 0xd3, // Ë
  0xc8: 0xd4, // È
  0xcd: 0xd6, // Í
  0xce: 0xd7, // Î
  0xcf: 0xd8, // Ï
  0xcc: 0xde, // Ì
  0xd3: 0xe0, // Ó
  0xdf: 0xe1, // ß
  0xd4: 0xe2, // Ô
  0xd2: 0xe3, // Ò
  0xf5: 0xe4, // õ
  0xd5: 0xe5, // Õ
  0xb5: 0xe6, // µ
  0xfe: 0xe7, // þ
  0xde: 0xe8, // Þ
  0xda: 0xe9, // Ú
  0xdb: 0xea, // Û
  0xd9: 0xeb, // Ù
  0xfd: 0xec, // ý
  0xdd: 0xed, // Ý
  0xb0: 0xf8, // °
  0xb7: 0xfa, // ·
  0xb2: 0xfd, // ²
  0xa7: 0xf5, // §
  0xbd: 0xab, // ½
  0xbc: 0xac, // ¼
};

const FALLBACK_BYTE = 0x3f; // "?"

/** Codifica texto a bytes CP858; lo no representable degrada a `?`. */
export function encodeCp858(text: string): number[] {
  const bytes: number[] = [];
  for (const ch of text) {
    const cp = ch.codePointAt(0) ?? FALLBACK_BYTE;
    if (cp === 0x0a) {
      bytes.push(0x0a);
    } else if (cp >= 0x20 && cp <= 0x7e) {
      bytes.push(cp);
    } else {
      bytes.push(CP858[cp] ?? FALLBACK_BYTE);
    }
  }
  return bytes;
}

export type EscPosAlign = "left" | "center" | "right";

/**
 * Builder de payload ESC/POS. Cada método agrega bytes; `payload()` devuelve
 * el Uint8Array final. Puro y determinista (sin I/O ni relojes).
 */
export class EscPosBuilder {
  private readonly bytes: number[] = [];

  /** `ESC @` — reset de la impresora + `ESC t 19` — codepage CP858. */
  init(): this {
    this.bytes.push(ESC, 0x40); // ESC @
    this.bytes.push(ESC, 0x74, 19); // ESC t 19 → CP858
    return this;
  }

  align(align: EscPosAlign): this {
    const n = align === "center" ? 1 : align === "right" ? 2 : 0;
    this.bytes.push(ESC, 0x61, n); // ESC a n
    return this;
  }

  bold(on: boolean): this {
    this.bytes.push(ESC, 0x45, on ? 1 : 0); // ESC E n
    return this;
  }

  /** `GS ! n` — 0 normal, 0x11 doble ancho+alto. */
  doubleSize(on: boolean): this {
    this.bytes.push(GS, 0x21, on ? 0x11 : 0x00);
    return this;
  }

  /** Texto + LF (una línea). */
  line(text = ""): this {
    this.bytes.push(...encodeCp858(text), 0x0a);
    return this;
  }

  /** Alimenta `n` líneas en blanco (`ESC d n`). */
  feed(n: number): this {
    this.bytes.push(ESC, 0x64, Math.max(0, Math.trunc(n)));
    return this;
  }

  /** Corte parcial (`GS V 66 0`). */
  cut(): this {
    this.bytes.push(GS, 0x56, 66, 0);
    return this;
  }

  /** Pulso de apertura de cajón (`ESC p 0 25 250`). */
  openDrawer(): this {
    this.bytes.push(ESC, 0x70, 0, 25, 250);
    return this;
  }

  payload(): Uint8Array {
    return Uint8Array.from(this.bytes);
  }
}
